import React, { useEffect, useState } from 'react';
import ApiService from '../../services/ApiService';
import { API_BASE_URL } from '../../config';
import styles from '../AdminPanel.module.css';

type Props = {
  onError?: (msg: string) => void;
};

type TierInfo = {
  tier_level: number;
  tier_name: string;
  monthly_price: number;
  advance_chapters: number;
  description: string | null;
};

type ApplicationItem = {
  novel_id: number;
  novel_title: string;
  novel_cover: string | null;
  author_name: string | null;
  author_id: number;
  author_username: string | null;
  pen_name: string | null;
  champion_status: string;
  approved_chapters: number;
  total_chapters: number;
  applied_at: string;
  tiers: TierInfo[];
};

const normalizeImageUrl = (url: string | null | undefined) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
  return url;
};

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function AdminChampionApproval({ onError }: Props) {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState<ApplicationItem[]>([]);
  const [toast, setToast] = useState<string>('');
  const [processingId, setProcessingId] = useState<number | null>(null);

  // 加载待审批列表
  const loadApplications = async () => {
    try {
      setLoading(true);
      const res = await ApiService.get('/champion/admin/pending-applications');
      const data = (res as any)?.data || [];
      setApplications(Array.isArray(data) ? data : []);
    } catch (e: any) {
      const msg = e?.message || '加载待审批列表失败';
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 显示提示信息
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // 审批通过
  const handleApprove = async (novelId: number, novelTitle: string) => {
    if (!window.confirm(`确定要通过 "${novelTitle}" 的Champion会员申请吗？`)) {
      return;
    }
    
    try {
      setProcessingId(novelId);
      await ApiService.post(`/champion/admin/approve/${novelId}`, {});
      showToast(`"${novelTitle}" 的Champion会员申请已通过`);
      // 刷新列表
      await loadApplications();
    } catch (e: any) {
      const msg = e?.message || '审批通过失败';
      onError?.(msg);
    } finally {
      setProcessingId(null);
    }
  };

  // 拒绝申请
  const handleReject = async (novelId: number, novelTitle: string) => {
    const reason = window.prompt(`请输入拒绝 "${novelTitle}" 的原因（可选）：`);
    if (reason === null) {
      return; // 用户点击了取消
    }
    
    try {
      setProcessingId(novelId);
      await ApiService.post(`/champion/admin/reject/${novelId}`, { reason });
      showToast(`"${novelTitle}" 的Champion会员申请已拒绝`);
      // 刷新列表
      await loadApplications();
    } catch (e: any) {
      const msg = e?.message || '拒绝申请失败';
      onError?.(msg);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className={styles.tabContent}>
      <h2>Champion会员审批</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        审批作者提交的Champion会员申请，通过后小说将开启Champion会员功能。
      </p>

      {/* Toast提示 */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#4caf50',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: '8px',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}>
          {toast}
        </div>
      )}

      {/* 统计信息 */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        background: 'var(--bg-secondary)',
        borderRadius: '8px'
      }}>
        <span style={{ fontWeight: 500 }}>
          待审批申请: <span style={{ color: '#f57c00' }}>{applications.length}</span> 件
        </span>
        <button
          onClick={loadApplications}
          disabled={loading}
          style={{
            padding: '6px 16px',
            background: 'var(--primary-color)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? '刷新中...' : '刷新列表'}
        </button>
      </div>

      {/* 加载状态 */}
      {loading && applications.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          加载中...
        </div>
      )}

      {/* 空状态 */}
      {!loading && applications.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          color: 'var(--text-secondary)',
          background: 'var(--bg-secondary)',
          borderRadius: '8px'
        }}>
          暂无待审批的Champion会员申请
        </div>
      )}

      {/* 申请列表 - 卡片式布局 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {applications.map((app) => (
          <div
            key={app.novel_id}
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              padding: '1.25rem',
              border: '1px solid var(--border-color)'
            }}
          >
            <div style={{ display: 'flex', gap: '1.25rem' }}>
              {/* 封面 */}
              <div style={{ flexShrink: 0 }}>
                {app.novel_cover ? (
                  <img
                    src={normalizeImageUrl(app.novel_cover)}
                    alt={app.novel_title}
                    style={{
                      width: '80px',
                      height: '110px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '80px',
                    height: '110px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-secondary)',
                    fontSize: '12px'
                  }}>
                    无封面
                  </div>
                )}
              </div>

              {/* 信息区域 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 小说标题和基本信息 */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '1.1rem', 
                    fontWeight: 600,
                    color: 'var(--text-primary)'
                  }}>
                    {app.novel_title}
                    <span style={{
                      marginLeft: '0.75rem',
                      fontSize: '0.75rem',
                      padding: '2px 8px',
                      background: '#ff9800',
                      color: '#fff',
                      borderRadius: '4px',
                      fontWeight: 400
                    }}>
                      待审批
                    </span>
                  </h3>
                  <div style={{ 
                    marginTop: '0.5rem', 
                    fontSize: '0.9rem', 
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem'
                  }}>
                    <span>作者: {app.pen_name || app.author_name || app.author_username || '-'}</span>
                    <span>章节: {app.approved_chapters}章 (已审核) / {app.total_chapters}章 (总计)</span>
                    <span>申请时间: {formatDate(app.applied_at)}</span>
                  </div>
                </div>

                {/* 会员等级配置 */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 500, 
                    marginBottom: '0.5rem',
                    color: 'var(--text-primary)'
                  }}>
                    会员等级配置:
                  </div>
                  {app.tiers.length > 0 ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                      gap: '0.5rem'
                    }}>
                      {app.tiers.map((tier) => (
                        <div
                          key={tier.tier_level}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: 'var(--bg-tertiary)',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid var(--border-color)'
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>
                            Lv{tier.tier_level} {tier.tier_name}
                          </span>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            ${tier.monthly_price}/月 · {tier.advance_chapters}章预读
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      color: 'var(--text-secondary)', 
                      fontSize: '0.85rem',
                      fontStyle: 'italic'
                    }}>
                      暂无配置的会员等级
                    </div>
                  )}
                </div>

                {/* 操作按钮 */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => window.open(`/book/${app.novel_id}`, '_blank')}
                    style={{
                      padding: '8px 16px',
                      background: 'transparent',
                      color: 'var(--primary-color)',
                      border: '1px solid var(--primary-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    查看详情
                  </button>
                  <button
                    onClick={() => handleApprove(app.novel_id, app.novel_title)}
                    disabled={processingId === app.novel_id}
                    style={{
                      padding: '8px 20px',
                      background: '#4caf50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: processingId === app.novel_id ? 'not-allowed' : 'pointer',
                      opacity: processingId === app.novel_id ? 0.6 : 1,
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    {processingId === app.novel_id ? '处理中...' : '通过审批'}
                  </button>
                  <button
                    onClick={() => handleReject(app.novel_id, app.novel_title)}
                    disabled={processingId === app.novel_id}
                    style={{
                      padding: '8px 20px',
                      background: '#f44336',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: processingId === app.novel_id ? 'not-allowed' : 'pointer',
                      opacity: processingId === app.novel_id ? 0.6 : 1,
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    拒绝申请
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
